"use client";

import { useEffect, useState, useRef } from "react";
import Image from "next/image";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowUpRightIcon,
} from "@heroicons/react/24/outline";
import LocalizedLink from "../LocalizedLink";
import Link from "next/link";

export interface ReviewsSocialProps {
  images: Array<{
    image: string | { url: string }; // Actually video
    url: string;
  }>;
  color?: "violet" | "green" | "blue";
}

export default function ReviewsSocial({
  images,
  color = "green",
}: ReviewsSocialProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [progressArray, setProgressArray] = useState<number[]>(
    Array(images.length).fill(0)
  );
  const imageCount = images.length;
  const delay = 3000; // 3 seconds
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const backgroundColorClass =
    {
      green: "custom-gradient-green",
      blue: "custom-gradient-blue",
      violet: "custom-gradient-violet",
    }[color] || "custom-gradient-green";

  const backgroundBtnColorClass =
    {
      green: "custom-gradient-green-btn-h",
      blue: "custom-gradient-blue-btn-h",
      violet: "custom-gradient-violet-btn-h",
    }[color] || "custom-gradient-green-btn-h";

  // Move to the next image
  const nextImage = () => {
    setCurrentIndex((currentIndex + 1) % imageCount);
    resetProgressExceptCurrent((currentIndex + 1) % imageCount);
  };

  // Reset progress for all images except the current one
  const resetProgressExceptCurrent = (newIndex: number) => {
    setProgressArray((prevProgressArray) => {
      return prevProgressArray.map((_, index) => (index === newIndex ? 0 : 0));
    });
  };

  // Clear interval and reset progress on manual button click
  const handleManualNavigation = (newIndex: number) => {
    clearTimeout(timeoutRef.current as NodeJS.Timeout);
    setCurrentIndex(newIndex);
    resetProgressExceptCurrent(newIndex);
    setProgressArray((prevProgressArray) => {
      const updatedProgress = [...prevProgressArray];
      updatedProgress[newIndex] = 0;
      return updatedProgress;
    });
  };

  // Auto-switch images
  useEffect(() => {
    const startProgress = () => {
      setProgressArray((prevProgressArray) => {
        const updatedProgress = [...prevProgressArray];
        if (updatedProgress[currentIndex] >= 100) {
          nextImage(); // Move to the next image only when the progress bar completes
          updatedProgress[currentIndex] = 0;
        } else {
          updatedProgress[currentIndex] += 100 / (delay / 100);
        }
        return updatedProgress;
      });
    };

    timeoutRef.current = setInterval(startProgress, 100);

    return () => {
      clearInterval(timeoutRef.current as NodeJS.Timeout);
    };
  }, [currentIndex]);

  return (
    <div
      data-meta="ReviewsSocial"
      className="lg:col-span-5 bg-white rounded-3xl py-6 px-4"
    >
      {/* Progress bars for each image */}
      <div className="flex space-x-2 mb-4">
        {images.map((_, index) => (
          <div key={index} className="w-full h-1 bg-gray-300 rounded-3xl">
            <div
              style={{ width: `${progressArray[index]}%` }}
              className={`h-full rounded-3xl ${backgroundColorClass}  transition-all duration-100`}
            />
          </div>
        ))}
      </div>

      {/* Navigation Buttons */}
      <div className="inset-0 flex justify-between px-4 pb-4">
        <div className="flex items-center gap-x-1">
          <div className="p-2 rounded-full  overflow-hidden">
            <Image
              src={"/assets/icon/EducationLogoSmall.svg"}
              alt={" "}
              width={32}
              height={32}
              className="h-8 w-8"
            />
          </div>
          <p className="font-bold">ap.education</p>
        </div>
        <div>
          <button
            title="Previous image"
            className={`p-2 text-${color}-400 hover:text-white ${backgroundBtnColorClass} rounded-full`}
            onClick={() =>
              handleManualNavigation(
                (currentIndex - 1 + imageCount) % imageCount
              )
            }
          >
            <ChevronLeftIcon className="h-6 w-6" />
          </button>
          <button
            title="Next image"
            className={`p-2 text-${color}-400 hover:text-white ${backgroundBtnColorClass} rounded-full`}
            onClick={() =>
              handleManualNavigation((currentIndex + 1) % imageCount)
            }
          >
            <ChevronRightIcon className="h-6 w-6" />
          </button>
        </div>
      </div>

      {/* Image display */}
      <div className="relative w-full h-[400px] bg-white rounded-lg overflow-hidden">
        {/* Images */}
        {images.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-transform duration-1000 ease-in-out ${
              index === currentIndex
                ? "translate-x-0 opacity-100"
                : "translate-x-full opacity-0"
            }`}
            style={{ transition: "transform 1s ease, opacity 1s ease" }}
          >
            <video
              className="flex justify-center cursor-pointer w-full h-full object-cover"
              src={typeof image.image === "string" ? image.image : image.image.url}
              title="AP Education"
              loop
              playsInline
              autoPlay
              controls={false}
              muted
            ></video>
            <Link
              aria-label="Open post"
              href={image.url}
              className="absolute left-0 right-0 top-0 bottom-0 cursor-pointer"
            ></Link>
            <div className="absolute bottom-2 left-2 backdrop-blur-sm bg-gradient-to-tr from-gray-900/30 to-white/30 rounded-full h-20 w-20 flex justify-center items-center">
              <ArrowUpRightIcon className="text-white h-12 w-12" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
